import type { dataType } from 'src/constants';
import type { ThemeData } from 'src/pages/procurer/preferences/Themes/schema';
import type { ImpactData, PreferencesStep } from 'src/store/preferencesStore';
import type { ContractPreferencesCurrentStep as CurrentStep } from 'src/types';
import type { ScoringData } from 'src/pages/procurer/preferences/Scoring/schema';
import type { ContractMetaData } from 'src/pages/procurer/contract/Start/schema';
import type { GeographyData } from 'src/pages/procurer/preferences/Geography/schema';
import type { FinalizingData } from 'src/pages/procurer/preferences/Finalizing/schema';
import type { QualitativeData } from 'src/pages/procurer/preferences/Qualitative/schema';
import type { ReportedActivityData } from 'src/pages/procurer/preferences/Impact/schema';

import { axios, formatError } from './utils';

interface SaveContractApiResponse {
  message: string;
  data: ContractData;
}
interface CreateContractApiResponse {
  message: string;
  data: ContractData & { id: string };
}

// Override GeographyData to use string[] for geographicalAreas
export type GeographyDataWithArray = Omit<GeographyData, 'geographicalAreas'> & {
  geographicalAreas: string[];
};
export interface AdditionalRequirements {
  id: number;
  title: string;
  type: string;
  delivered: number;
  units: number;
  details: string;
  hasUnits: boolean;
}

export type ReportStatus = dataType.ReportStatus;

export interface Report {
  id: number;
  submittedBy: string;
  submittedAt: string;
  status: ReportStatus;
  approvedAt?: string;
  changesRequestedAt?: string;
  changesDueAt?: string;
  reportPeriodStart: string;
  reportPeriodEnd: string;
  hasDraft?: boolean;
}

export interface ReportsFromSupplier {
  previousReportDue: string;
  currentReportDue: string;
  canSubmit: boolean;
  nextReportDue: string;
  reports: Report[];
}

export interface ContractStats {
  committed: {
    impactValue: number;
    marketValue: number;
    points: number;
  };
  reported: {
    impactValue: number;
    marketValue: number;
    points: number;
  };
}

export interface ContractAnnualCycle {
  year: number;
  startDate: string;
  endDate: string;
  months: number;
  isStub: boolean;
}

export interface ContractFilterContext {
  requestedStartDate: string | null;
  requestedEndDate: string | null;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  contractStartDate: string;
  contractEndDate: string;
  proRataRatio: number;
  isOutsideContract: boolean;
  isPartialContractOverlap: boolean;
  hasPartialReportingPeriodOverlap: boolean;
  annualCycles: ContractAnnualCycle[];
}

export interface ContractScheduleDetails {
  rrule?: string | null;
}

export interface ContractData {
  details: ContractMetaData & ContractScheduleDetails & { buyerName?: string };
  currentStep: CurrentStep;
  geography?: GeographyDataWithArray;
  themes?: ThemeData[];
  impact?: ImpactData | null;
  scoring?: ScoringData | null;
  qualitative?: QualitativeData | null;
  finalizing?: FinalizingData;
  completedSteps?: PreferencesStep[];
  extraActivities?: ReportedActivityData[];
  additionalRequirements?: AdditionalRequirements[];
  reportsFromSupplier?: ReportsFromSupplier;
  stats?: ContractStats;
  filterContext?: ContractFilterContext;
  inviteBinding?: InviteBinding;
  error?: any;
}

export interface InviteBinding {
  hasLinkedSupplier: boolean;
  supplierEmail?: string | null;
  supplierName?: string | null;
}

export async function getContract(
  contractId: string | undefined,
  params?: { startDate?: string | null; endDate?: string | null }
): Promise<ContractData> {
  try {
    const response = await axios.get(`/api/contract/${contractId}`, {
      params: {
        startDate: params?.startDate ?? undefined,
        endDate: params?.endDate ?? undefined,
      },
    });
    return response.data.data;
  } catch (error) {
    const err = formatError(error);
    throw err;
  }
}

export async function createContract(
  data: ContractMetaData & { sessionId: string }
): Promise<CreateContractApiResponse> {
  try {
    const response = await axios.post('/api/contract', data);
    return response.data;
  } catch (error) {
    const err = formatError(error);
    throw err;
  }
}

export async function updateContract(
  data: ContractMetaData & { sessionId: string }
): Promise<CreateContractApiResponse> {
  try {
    const response = await axios.patch(`/api/contract/${data.id}`, data);
    return response.data;
  } catch (error) {
    const err = formatError(error);
    throw err;
  }
}

export async function saveContractSetup(
  data: ContractData & { id: string; sessionId: string }
): Promise<SaveContractApiResponse> {
  try {
    if (!data.id) {
      throw new Error('Contract ID is required');
    }
    const response = await axios.patch(`/api/contract/${data.id}/setup`, data);
    return response.data;
  } catch (error) {
    const err = formatError(error);
    throw err;
  }
}

export interface AnnualBreakdownEdit {
  activityId: number;
  unitsBreakdown: number[];
  reason: string;
}

export async function updateAnnualBreakdown(data: {
  contractId: string;
  sessionId: string;
  edits: AnnualBreakdownEdit[];
}): Promise<{ message: string }> {
  try {
    const { contractId, ...body } = data;
    const response = await axios.patch(`/api/contract/${contractId}/annual-breakdown`, body);
    return response.data;
  } catch (error) {
    const err = formatError(error);
    throw err;
  }
}

export type ContractSortKey = 'impact' | 'market' | 'points' | 'end_date';
export type ContractSortDirection = 'asc' | 'desc';

export interface DashboardContract {
  id: number;
  title: string;
  startDate: string;
  endDate: string;
  commitmentPeriod: string | null;
  contractValue: number;
  supplierName: string;
  contractLeadName?: string | null;
  contractLeadEmail?: string | null;
  supplierOrganisation?: string;
  createdAt: string;
  updatedAt: string;
  rrule?: string | null;
  procurer?: {
    id: number;
    firstName: string;
    lastName: string;
    organisation: string;
  };
  impactValues: {
    reported: number;
    committed: number;
  } | null;
  marketValues: {
    reported: number;
    committed: number;
  } | null;
  pointsValues: {
    reported: number;
    committed: number;
  } | null;
  lastReport: { date: string; status: string } | null;
  // #634: non-null means this contract was set up in backfill mode. There is no separate
  // boolean — derive with `!!backfillCutoffDate` so the two can never drift apart.
  backfillCutoffDate?: string | null;
  // Whether backfilled reports are actually in scope of the current filters. Warnings key off
  // this, not the cut-off: choosing a cut-off doesn't yet understate any figure.
  hasBackfillReports?: boolean;
}

export interface GetDashboardContractsParams extends ContractDashboardAdditionalFilters {
  procurerId: string | number;
  page?: number;
  limit?: number;
  sortBy?: ContractSortKey;
  sortDirection?: ContractSortDirection;
}

export type DashboardStats = {
  committed: {
    marketValue: number;
    impactValue: number;
    points: number;
  };
  reported: {
    marketValue: number;
    impactValue: number;
    points: number;
  };
  totalContractValue: number;
  totalCommitmentsMade: number;
  spendInLocalSupply: number;
};
export type DashboardActivityDeliverySummary = {
  id: number;
  activity: string;
  unitType: string;
  totalUnitsCommitted: number;
  totalUnitsDelivered: number;
};

export interface GetDashboardContractsResponse {
  contracts: DashboardContract[];
  activities: DashboardActivityDeliverySummary[];
  totalCount: number;
  hasBackfilledContract: boolean;
  dashboardStats: DashboardStats;
  visibilityPreferences: ScoringData['visibilityPreferences'];
}

export const getDashboardContracts = async ({
  page = 1,
  limit = 20,
  sortBy = 'end_date',
  sortDirection = 'desc',
  ...additionalFilters
}: GetDashboardContractsParams): Promise<GetDashboardContractsResponse> => {
  const offset = (page - 1) * limit;

  const response = await axios.get('/api/contract/dashboard', {
    params: {
      limit,
      offset,
      sortBy,
      sortDirection,
      ...additionalFilters,
    },
  });

  return response.data;
};

export const getPublicDashboardContracts = async ({
  procurerId,
  page = 1,
  limit = 20,
  sortBy = 'end_date',
  sortDirection = 'desc',
  ...additionalFilters
}: GetDashboardContractsParams): Promise<GetDashboardContractsResponse> => {
  const offset = (page - 1) * limit;

  const response = await axios.get(`/api/contract/dashboard/public/${procurerId}`, {
    params: {
      limit,
      offset,
      sortBy,
      sortDirection,
      ...additionalFilters,
    },
  });

  return response.data;
};

export interface GetDashboardContractSuppliersParams {
  procurerId: string | number | undefined;
}

export const getDashboardContractSuppliers = async ({
  procurerId,
}: GetDashboardContractSuppliersParams): Promise<{
  suppliers: string[];
}> => {
  const response = await axios.get(`/api/contract/suppliers/${procurerId}`);

  return response.data;
};

export interface GetDashboardContractProcurersParams {
  supplierId: number | undefined;
}

export const getDashboardContractProcurers = async ({
  supplierId,
}: GetDashboardContractProcurersParams): Promise<{
  procurers: string[];
}> => {
  const response = await axios.get(`/api/contract/procurers/${supplierId}`);

  return response.data;
};

export interface DashboardFilterOptions {
  geographicBoundaries: string[];
  geographicalAreas: string[];
  contractTypes: string[];
  departments: string[];
  communityPartners: string[];
  roleTypes: string[];
}

export const getDashboardFilterOptions = async (): Promise<DashboardFilterOptions> => {
  const response = await axios.get('/api/contract/dashboard/filter-options');
  return response.data;
};

export interface ContractDashboardAdditionalFilters {
  suppliers?: string[] | null;
  startDate?: string | null;
  endDate?: string | null;
  contractStatus?: 'completed' | 'in_progress' | null;
  includeAddedValue?: boolean;
  geographicalAreas?: string[] | null;
}
export interface DashboardStatsParams extends ContractDashboardAdditionalFilters {
  procurerId: string | number | undefined;
}

export interface ContractsDashboardStatsResponse {
  deliveredImpactValue?: number;
  committedImpactValue?: number;
  deliveredMarketValue?: number;
  committedMarketValue?: number;
  deliveredPoints?: number;
  committedPoints?: number;
  totalContractValue?: number;
  totalCommitmentsMade?: number;
  spendInLocalSupply?: number;
}

export const getContractsDashboardStats = async (
  params: DashboardStatsParams
): Promise<ContractsDashboardStatsResponse> => {
  const { suppliers, startDate, endDate, contractStatus, includeAddedValue = true } = params;

  const response = await axios.get<ContractsDashboardStatsResponse>(`/api/contract/stats`, {
    params: {
      suppliers: suppliers ?? undefined,
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
      contractStatus: contractStatus ?? undefined,
      includeAddedValue,
    },
  });

  return response.data;
};

export const getPublicContractsDashboardStats = async (
  params: DashboardStatsParams
): Promise<ContractsDashboardStatsResponse> => {
  const {
    procurerId,
    suppliers,
    startDate,
    endDate,
    contractStatus,
    includeAddedValue = true,
  } = params;

  const response = await axios.get<ContractsDashboardStatsResponse>(
    `/api/contract/public/${procurerId}/stats`,
    {
      params: {
        suppliers: suppliers ?? undefined,
        startDate: startDate ?? undefined,
        endDate: endDate ?? undefined,
        contractStatus: contractStatus ?? undefined,
        includeAddedValue,
      },
    }
  );

  return response.data;
};

export interface ContractInviteInfo {
  procurer: {
    name: string;
  };
  contract: {
    id: number;
    title: string;
    applicationStartDate: string;
    applicationEndDate: string;
    applicationWindowStatus: 'not_started' | 'open' | 'closed';
    isExistingContract: boolean;
    isOpenForTenders: boolean;
  };
  existingApplication?: {
    id: number;
    status: string;
  };
}

export async function getContractByToken(token: string): Promise<ContractInviteInfo> {
  try {
    const response = await axios.get(`/api/contract/invite/${token}`);
    return response.data.data;
  } catch (error) {
    throw formatError(error);
  }
}

export interface ContractOpenForTender {
  id: number;
  title: string;
  applicationStartDate: string;
  applicationEndDate: string;
  contractValue: number;
}

export interface SupplierContractOpenForTender {
  id: number;
  title: string;
  applicationEndDate: string;
  status: keyof typeof dataType.SupplierApplicationDisplayStatus;
  changeRequests: { changesDueAt: string }[];
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
}

export const getContractsOpenForTenderGeneric = async <
  T = ContractOpenForTender | SupplierContractOpenForTender,
>(
  page: number,
  limit: number
): Promise<PaginatedResponse<T>> => {
  const res = await axios.get<PaginatedResponse<T>>('/api/contract/open-for-tender', {
    params: { page, limit },
  });
  return res.data;
};

export type ApplicationStatus = 'Reviewed' | 'Awaiting Review' | 'Rejected' | 'Submitted';

export interface ContractOpenForTenderDetail {
  id: number;
  title: string;
  applicationStartDate: string; // ISO
  applicationEndDate: string; // ISO
  type: string; // e.g. "Fixed-term contract"
  publicToken?: string;
}

export const getContractOpenForTenderById = async (
  contractId: number
): Promise<ContractOpenForTenderDetail> => {
  const { data } = await axios.get(`/api/contract/open-for-tender/${contractId}`);
  return data;
};

export type SortBy =
  | 'supplierName'
  | 'status'
  | 'points'
  | 'marketValue'
  | 'impactValue'
  | 'qualitativeScore'
  | 'totalScore'
  | 'submittedAt';

export type SortDirection = 'asc' | 'desc';

export const getContractApplications = async (
  contractId: number,
  page: number,
  limit: number,
  sortBy: SortBy,
  sortDirection: SortDirection
) => {
  try {
    const { data } = await axios.get(`/api/contract/open-for-tender/${contractId}/applications`, {
      params: { page, limit, sortBy, sortDirection },
    });
    return data as {
      data: Array<{
        id: number;
        supplierName: string;
        status: string;
        points: number | null;
        marketValue: number | null;
        impactValue: number | null;
        qualitativeScore: number | null;
        totalScore: number | null;
        submittedAt: string | null;
        hasReachedSubmitEndDay: boolean;
        hasDraft: boolean;
        hasPendingChangeRequest: boolean;
      }>;
      pagination: { page: number; limit: number; totalCount: number; totalPages: number };
      normalisation: {
        maxPoints: number | null;
        maxMarketValue: number | null;
        maxImpactValue: number | null;
        maxQualitativeScore: number | null;
      };
      canAwardContract: boolean;
    };
  } catch (error) {
    const err = formatError(error);
    throw err;
  }
};

export interface ReviewedApplication {
  message: string;
  data: Array<{
    id: number;
    name: string;
    status: string;
    impactValue: number | null;
    marketValue: number | null;
    points: number | null;
    qualitativeScore: number | null;
    totalScore: number | null;
  }>;
}

export const getReviewedApplications = async (contractId: number): Promise<ReviewedApplication> => {
  const { data } = await axios.get(`/api/contract/${contractId}/reviewed-applications`);
  return data;
};

export interface AwardContractRequest {
  applicationId: number;
  supplierName?: string;
}

export interface AwardContractResponse {
  message: string;
  data: {
    applicationId: number;
  };
}

export const awardContract = async (
  contractId: number,
  data: AwardContractRequest
): Promise<AwardContractResponse> => {
  try {
    const response = await axios.patch(`/api/contract/${contractId}/award`, data);
    return response.data;
  } catch (error) {
    throw formatError(error);
  }
};

export interface AwardedApplication {
  id: number;
  contractId: number;
  userId: number;
  supplierContractLeadName: string;
  supplierContractLeadEmail: string;
  supplierSocialValueLeadName: string;
  supplierSocialValueLeadEmail: string;
  leadName: string;
  leadEmail: string;
  socialValueLeadName: string;
  socialValueLeadEmail: string;
  supplierName: string;
  supplierOrganisation: string;
  supplierEmail: string;
  contractTitle: string;
  contractType: string;
  contractValue: number;
  contractStart: string;
  contractEnd: string;
  rrule?: string | null;
  isFinalized?: boolean;
}

export interface AwardedContractDataResponse {
  message: string;
  data?: {
    application: AwardedApplication | null;
    activities: {
      totalPoints: number;
      totalMarketValue: number;
      totalImpactValue: number;
    };
    additionalRequirements: AdditionalRequirements[];
  };
}

export const getAwardedContractData = async (
  contractId: number
): Promise<AwardedContractDataResponse> => {
  try {
    const response = await axios.get(`/api/contract/${contractId}/awarded-data`);
    return response.data;
  } catch (error) {
    throw formatError(error);
  }
};

export interface FinalizeAwardedContractRequest {
  additionalRequirements: Array<{
    id: number;
    type: string;
    title: string;
    details: string;
    hasUnits: boolean;
    units?: number;
  }>;
  reportingData: {
    rrule: string;
  };
}

export interface FinalizeAwardedContractResponse {
  message: string;
}

export const finalizeAwardedContract = async (
  contractId: number,
  sessionId: string,
  data: FinalizeAwardedContractRequest
): Promise<FinalizeAwardedContractResponse> => {
  try {
    const response = await axios.patch(`/api/contract/${contractId}/awarded-contract`, {
      requirements: data.additionalRequirements,
      reportingData: data.reportingData,
      sessionId,
    });
    return response.data;
  } catch (error) {
    throw formatError(error);
  }
};
